import { useEffect, useState, useCallback } from "react";
import { CalendarCheck, DollarSign, Bell, ClipboardList, UserPlus, Trash2, MapPin, Phone, Clock } from "lucide-react";
import { toast } from "sonner";
import { DashboardLayout } from "@/components/DashboardLayout";
import { api, formatApiError } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const STATUS_META = {
  booked: { label: "Booked", className: "bg-gray-200 text-gray-800" },
  on_the_way: { label: "On the way", className: "bg-blue-100 text-blue-800" },
  in_progress: { label: "In progress", className: "bg-harvest/30 text-yellow-900" },
  completed: { label: "Completed", className: "bg-leaf/20 text-leaf-dark" },
};

export const StatusBadge = ({ status }) => (
  <Badge className={`rounded-full px-3 py-1 text-xs font-bold border-0 ${STATUS_META[status]?.className || ""}`} data-testid={`status-badge-${status}`}>
    {STATUS_META[status]?.label || status}
  </Badge>
);

const KpiCard = ({ icon: Icon, label, value, testid }) => (
  <Card className="rounded-2xl border-border" data-testid={testid}>
    <CardContent className="p-4 sm:p-5 flex items-center gap-3 sm:gap-4">
      <span className="h-10 w-10 sm:h-11 sm:w-11 rounded-xl bg-secondary flex items-center justify-center shrink-0">
        <Icon className="h-5 w-5 text-primary" />
      </span>
      <div className="min-w-0">
        <p className="text-[10px] sm:text-xs uppercase tracking-[0.15em] font-semibold text-muted-foreground truncate">{label}</p>
        <p className="text-xl sm:text-2xl font-display font-bold truncate" data-testid={`${testid}-value`}>{value}</p>
      </div>
    </CardContent>
  </Card>
);

export default function OwnerDashboard() {
  const [summary, setSummary] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [tab, setTab] = useState("today");
  const [empForm, setEmpForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [addingEmp, setAddingEmp] = useState(false);
  const [empDialogOpen, setEmpDialogOpen] = useState(false);

  const todayStr = new Date().toLocaleDateString("en-CA", { timeZone: "Australia/Sydney" });

  const load = useCallback(async () => {
    try {
      const [s, b, e, r] = await Promise.all([
        api.get("/dashboard/summary"),
        api.get("/bookings"),
        api.get("/employees"),
        api.get("/reviews/all"),
      ]);
      setSummary(s.data);
      setBookings(b.data.bookings);
      setEmployees(e.data.employees);
      setReviews(r.data.reviews);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const assign = async (bookingId, employeeId) => {
    try {
      await api.patch(`/bookings/${bookingId}/assign`, { employee_id: employeeId === "unassigned" ? null : employeeId });
      toast.success("Assignment updated");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const setStatus = async (bookingId, status) => {
    try {
      await api.patch(`/bookings/${bookingId}/status`, { status });
      toast.success("Status updated");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const togglePayment = async (b) => {
    try {
      await api.patch(`/bookings/${b.id}/payment`, { payment_status: b.payment_status === "paid" ? "pending" : "paid" });
      toast.success(b.payment_status === "paid" ? "Marked as unpaid" : "Payment marked as received");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const confirmSchedule = async (bookingId, date, time_slot) => {
    try {
      await api.patch(`/bookings/${bookingId}/schedule`, { date, time_slot });
      toast.success("Date confirmed — customer notified by email");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const savePrice = async (bookingId, price) => {
    try {
      await api.patch(`/bookings/${bookingId}/price`, { final_price: Number(price) });
      toast.success("Price set — customer notified by email");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const setReviewApproval = async (reviewId, approved) => {
    try {
      await api.patch(`/reviews/${reviewId}`, { approved });
      toast.success(approved ? "Review published" : "Review hidden");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const removeReview = async (reviewId) => {
    try {
      await api.delete(`/reviews/${reviewId}`);
      toast.success("Review deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const addEmployee = async (e) => {
    e.preventDefault();
    setAddingEmp(true);
    try {
      await api.post("/employees", empForm);
      toast.success(`${empForm.name} added`);
      setEmpForm({ name: "", email: "", phone: "", password: "" });
      setEmpDialogOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAddingEmp(false);
    }
  };

  const removeEmployee = async (emp) => {
    try {
      await api.delete(`/employees/${emp.id}`);
      toast.success(`${emp.name} removed`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const unscheduledCount = bookings.filter((b) => !b.date && b.status !== "completed").length;

  const filtered = bookings.filter((b) => {
    if (tab === "today") return b.date === todayStr;
    if (tab === "unscheduled") return !b.date && b.status !== "completed";
    if (tab === "upcoming") return b.date && b.date >= todayStr && b.status !== "completed";
    return true;
  });

  return (
    <DashboardLayout title="Today at a glance">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-8" data-testid="kpi-grid">
        <KpiCard icon={CalendarCheck} label="Jobs today" value={summary?.jobs_today_count ?? "–"} testid="kpi-jobs-today" />
        <KpiCard icon={DollarSign} label="Revenue today" value={summary ? `$${summary.revenue_today}` : "–"} testid="kpi-revenue-today" />
        <KpiCard icon={Bell} label="New (24h)" value={summary?.new_bookings_24h ?? "–"} testid="kpi-new-bookings" />
        <KpiCard icon={ClipboardList} label="Upcoming" value={summary?.upcoming_count ?? "–"} testid="kpi-upcoming" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="flex gap-2 mb-4" data-testid="jobs-filter-tabs">
            {[["today", "Today"], ["unscheduled", `Needs date${unscheduledCount ? ` (${unscheduledCount})` : ""}`], ["upcoming", "Upcoming"], ["all", "All jobs"]].map(([id, label]) => (
              <button key={id} onClick={() => setTab(id)} data-testid={`jobs-tab-${id}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors min-h-[40px] ${tab === id ? "bg-primary text-white" : "bg-white border border-border text-muted-foreground hover:text-foreground"}`}>
                {label}
              </button>
            ))}
          </div>

          <div className="space-y-4" data-testid="owner-jobs-list">
            {filtered.length === 0 && (
              <Card className="rounded-2xl border-border"><CardContent className="p-8 text-sm text-muted-foreground" data-testid="jobs-empty">No jobs in this view yet.</CardContent></Card>
            )}
            {filtered.map((b, i) => (
              <Card key={b.id} className="rounded-2xl border-border animate-fade-up" style={{ animationDelay: `${Math.min(i * 0.04, 0.3)}s` }} data-testid={`job-card-${b.ref}`}>
                <CardContent className="p-5">
                  <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-display font-bold">{b.service_name}</span>
                        <StatusBadge status={b.status} />
                        {!b.assigned_to && b.status === "booked" && (
                          <Badge className="rounded-full px-3 py-1 text-xs font-bold bg-accent/15 text-accent border-0" data-testid={`new-badge-${b.ref}`}>New</Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">{b.ref} &middot; {b.name} &middot; {b.phone}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-display font-bold text-lg" data-testid={`job-price-${b.ref}`}>${b.final_price ?? b.customer_estimate ?? b.estimated_price}</p>
                      {b.final_price == null && b.customer_estimate != null && <p className="text-xs text-muted-foreground">customer&apos;s estimate</p>}
                      {b.final_price != null && <p className="text-xs text-muted-foreground">est. ${b.customer_estimate ?? b.estimated_price}</p>}
                      <button onClick={() => togglePayment(b)} data-testid={`payment-toggle-${b.ref}`}
                        title={b.payment_status === "paid" ? "Payment received — click to mark unpaid" : "Click to mark payment as received"}
                        className={`text-xs font-bold px-3 py-1 rounded-full transition-colors min-h-[28px] ${b.payment_status === "paid" ? "bg-leaf/20 text-leaf-dark" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
                        {b.payment_status === "paid" ? "PAID (undo)" : "MARK PAID"}
                      </button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-muted-foreground mb-4">
                    {b.date && (
                      <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {b.date} &middot; {b.time_slot}</span>
                    )}
                    <span className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> {b.address}</span>
                  </div>
                  {!b.date && (
                    <form onSubmit={(e) => { e.preventDefault(); const fd = new FormData(e.currentTarget); confirmSchedule(b.id, fd.get("date"), fd.get("time")); }}
                      className="flex flex-wrap items-center gap-2 mb-4 bg-harvest/15 border border-harvest/40 rounded-xl p-3" data-testid={`schedule-form-${b.ref}`}>
                      <span className="text-xs font-bold text-yellow-900 uppercase tracking-wide w-full">Needs a date — lock it in</span>
                      <Input name="date" type="date" required min={todayStr} className="w-40 h-10 rounded-xl bg-white" data-testid={`schedule-date-${b.ref}`} />
                      <select name="time" required defaultValue="" className="h-10 rounded-xl border border-input bg-white px-3 text-sm" data-testid={`schedule-time-${b.ref}`}>
                        <option value="" disabled>Time...</option>
                        {["8:00 AM", "10:00 AM", "12:00 PM", "2:00 PM", "4:00 PM", "Flexible"].map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                      <Button type="submit" size="sm" className="rounded-xl h-10 bg-primary hover:bg-[#21421D] text-white transition-colors" data-testid={`schedule-save-${b.ref}`}>
                        Confirm date
                      </Button>
                    </form>
                  )}
                  {b.details && <p className="text-sm bg-secondary rounded-lg p-3 mb-4">{b.details}</p>}
                  <div className="flex flex-wrap gap-3">
                    <Select value={b.assigned_to || "unassigned"} onValueChange={(v) => assign(b.id, v)}>
                      <SelectTrigger className="w-48 rounded-xl" data-testid={`assign-select-${b.ref}`}>
                        <SelectValue placeholder="Assign employee" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unassigned" data-testid={`assign-option-unassigned-${b.ref}`}>Unassigned</SelectItem>
                        {employees.map((e) => (
                          <SelectItem key={e.id} value={e.id} data-testid={`assign-option-${e.id}-${b.ref}`}>{e.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={b.status} onValueChange={(v) => setStatus(b.id, v)}>
                      <SelectTrigger className="w-44 rounded-xl" data-testid={`status-select-${b.ref}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(STATUS_META).map(([id, m]) => (
                          <SelectItem key={id} value={id} data-testid={`status-option-${id}-${b.ref}`}>{m.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {b.customer_estimate != null ? (
                      <form onSubmit={(e) => { e.preventDefault(); savePrice(b.id, new FormData(e.currentTarget).get("price")); }}
                        className="flex items-center gap-2">
                        <Input key={b.final_price ?? b.customer_estimate} name="price" type="number" min="0" step="1"
                          defaultValue={b.final_price ?? b.customer_estimate}
                          className="w-28 h-10 rounded-xl" data-testid={`price-input-${b.ref}`} />
                        <Button type="submit" size="sm" variant="outline" className="rounded-xl h-10" data-testid={`price-save-${b.ref}`}>
                          Set price
                        </Button>
                      </form>
                    ) : (
                      <span className="text-xs text-muted-foreground italic" data-testid={`price-waiting-${b.ref}`}>Waiting for customer estimate</span>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        <div className="space-y-6">
          <Card className="rounded-2xl border-border" data-testid="team-card">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-medium">Team</h2>
                <Dialog open={empDialogOpen} onOpenChange={setEmpDialogOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm" className="rounded-full bg-primary hover:bg-[#21421D] text-white transition-colors" data-testid="add-employee-button">
                      <UserPlus className="h-4 w-4 mr-1" /> Add
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="rounded-2xl" data-testid="add-employee-dialog">
                    <DialogHeader>
                      <DialogTitle>Add an employee</DialogTitle>
                      <DialogDescription>They will sign in with this email and password to see their assigned jobs.</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={addEmployee} className="space-y-4">
                      <div>
                        <Label htmlFor="emp-name">Name</Label>
                        <Input id="emp-name" data-testid="emp-name-input" required value={empForm.name} onChange={(e) => setEmpForm({ ...empForm, name: e.target.value })} className="h-11 mt-1 rounded-xl" />
                      </div>
                      <div>
                        <Label htmlFor="emp-email">Email</Label>
                        <Input id="emp-email" data-testid="emp-email-input" type="email" required value={empForm.email} onChange={(e) => setEmpForm({ ...empForm, email: e.target.value })} className="h-11 mt-1 rounded-xl" />
                      </div>
                      <div>
                        <Label htmlFor="emp-phone">Mobile</Label>
                        <Input id="emp-phone" data-testid="emp-phone-input" value={empForm.phone} onChange={(e) => setEmpForm({ ...empForm, phone: e.target.value })} className="h-11 mt-1 rounded-xl" />
                      </div>
                      <div>
                        <Label htmlFor="emp-password">Temporary password</Label>
                        <Input id="emp-password" data-testid="emp-password-input" required minLength={6} value={empForm.password} onChange={(e) => setEmpForm({ ...empForm, password: e.target.value })} className="h-11 mt-1 rounded-xl" />
                      </div>
                      <Button type="submit" disabled={addingEmp} className="w-full rounded-full bg-primary hover:bg-[#21421D] text-white transition-colors" data-testid="emp-submit-button">
                        {addingEmp ? "Adding..." : "Add employee"}
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
              {employees.length === 0 && <p className="text-sm text-muted-foreground" data-testid="team-empty">No employees yet. Add your first team member to start assigning jobs.</p>}
              <div className="space-y-3">
                {(summary?.employees || []).map((e) => (
                  <div key={e.id} className="flex items-center justify-between bg-secondary rounded-xl px-4 py-3" data-testid={`employee-row-${e.id}`}>
                    <div>
                      <p className="text-sm font-semibold">{e.name}</p>
                      <p className="text-xs text-muted-foreground">{e.jobs_today} job{e.jobs_today === 1 ? "" : "s"} today</p>
                    </div>
                    <button onClick={() => removeEmployee(e)} className="text-muted-foreground hover:text-destructive transition-colors" data-testid={`remove-employee-${e.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-border" data-testid="help-card">
            <CardContent className="p-5">
              <h2 className="text-lg font-medium mb-2">New enquiry notifications</h2>
              <p className="text-sm text-muted-foreground">
                You receive an email (and SMS once configured) for every new booking. Customers get instant confirmations and a reminder the day before their job.
              </p>
              <a href="tel:+61455530779" className="flex items-center gap-2 text-sm font-semibold text-primary mt-4" data-testid="dashboard-call-link">
                <Phone className="h-4 w-4" /> 0455 530 779
              </a>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-border" data-testid="reviews-card">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-medium">Reviews</h2>
                {reviews.filter((r) => !r.approved).length > 0 && (
                  <Badge className="rounded-full bg-accent/15 text-accent border-0" data-testid="reviews-pending-badge">
                    {reviews.filter((r) => !r.approved).length} to approve
                  </Badge>
                )}
              </div>
              {reviews.length === 0 && (
                <p className="text-sm text-muted-foreground" data-testid="reviews-admin-empty">No reviews yet.</p>
              )}
              <div className="space-y-3">
                {reviews.map((r) => (
                  <div key={r.id} className="bg-secondary rounded-xl p-3" data-testid={`admin-review-${r.id}`}>
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <p className="text-sm font-semibold">{r.name}</p>
                      <span className="text-xs text-harvest font-bold">{"★".repeat(r.rating)}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mb-2">{r.text}</p>
                    <div className="flex items-center gap-2 flex-wrap">
                      {r.approved ? (
                        <>
                          <Badge className="rounded-full bg-leaf/20 text-leaf-dark border-0 text-xs" data-testid={`review-live-${r.id}`}>Live</Badge>
                          <Button size="sm" variant="outline" className="rounded-full h-8" onClick={() => setReviewApproval(r.id, false)} data-testid={`review-hide-${r.id}`}>
                            Hide
                          </Button>
                        </>
                      ) : (
                        <Button size="sm" className="rounded-full h-8 bg-primary hover:bg-[#21421D] text-white transition-colors"
                          onClick={() => setReviewApproval(r.id, true)} data-testid={`review-approve-${r.id}`}>
                          Approve
                        </Button>
                      )}
                      <Button size="sm" variant="outline" className="rounded-full h-8 text-destructive"
                        onClick={() => removeReview(r.id)} data-testid={`review-delete-${r.id}`}>
                        Delete
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}
